import { Injectable, Inject } from '@angular/core';
import { InjectionToken } from '@angular/core';

export const STORAGE_PREFIX = new InjectionToken<string>('STORAGE_PREFIX');

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private storage = localStorage;

  constructor(@Inject(STORAGE_PREFIX) private prefix: string) {
    console.log('[Storage] Initialized with prefix:', prefix);
  }

  private getKey(key: string): string {
    return `${this.prefix}_${key}`;
  }

  async get(key: string): Promise<string | null> {
    try {
      const prefixedKey = this.getKey(key);
      // console.log('[Storage] Getting', prefixedKey);
      return this.storage.getItem(prefixedKey);
    } catch (error) {
      console.error('[Storage] Error getting item:', key, error);
      return null;
    }
  }

  async set(key: string, value: string): Promise<void> {
    try {
      const prefixedKey = this.getKey(key);
      // console.log('[Storage] Setting', prefixedKey, value.substring(0, 50) + (value.length > 50 ? '...' : ''));
      this.storage.setItem(prefixedKey, value);
    } catch (error) {
      console.error('[Storage] Error setting item:', key, error);
    }
  }

  async remove(key: string): Promise<void> {
    try {
      const prefixedKey = this.getKey(key);
      console.log('[Storage] Removing', prefixedKey);
      this.storage.removeItem(prefixedKey);
    } catch (error) {
      console.error('[Storage] Error removing item:', key, error);
    }
  }

  async clear(): Promise<void> {
    try {
      // Only clear items that match our prefix
      const keysToRemove: string[] = [];
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i);
        if (key && key.startsWith(`${this.prefix}_`)) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => {
        this.storage.removeItem(key);
      });

      console.log('[Storage] Cleared all items with prefix:', this.prefix);
    } catch (error) {
      console.error('[Storage] Error clearing storage:', error);
    }
  }

  cleanupLegacyStorage() {
    // CRITICAL FIX: DO NOT DELETE events_events - it's the main storage location!
    const legacyKeys = [
      'events_attendance',
      // 'events_events', // REMOVED - this is NOT legacy, it's the main storage!
      'events_events_attendance',
      'activeEventId'
    ];

    legacyKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log(`[Storage] Removed legacy storage key: ${key}`);
      }
    });

    console.log(`[Storage] ✅ Legacy cleanup complete - preserved events_events key`);
  }

  /**
   * Comprehensive cleanup of all storage inconsistencies
   * This method cleans up duplicate events and orphaned storage keys
   */
  async cleanupAllStorage(): Promise<void> {
    try {
      console.log('[Storage] 🧹 Starting comprehensive storage cleanup...');

      // 1. Get all current events from the main storage
      const eventsData = await this.get('events');
      const currentEvents: any[] = eventsData ? JSON.parse(eventsData) : [];
      const validEventIds = new Set(currentEvents.map(e => e.id));

      console.log(`[Storage] Found ${currentEvents.length} valid events:`, Array.from(validEventIds));

      // 2. Find all localStorage keys
      const allKeys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) allKeys.push(key);
      }

      // 3. Identify orphaned keys (keys for events that no longer exist)
      const orphanedKeys: string[] = [];
      const eventKeyPatterns = [
        /^event_(\d+)$/,                // event_1234567890
        /^events_count_(\d+)$/,         // events_count_1234567890  
        /^events_device_count_(\d+)$/,  // events_device_count_1234567890
        /^token_(\d+)$/,                // token_1234567890
        /^attendance_(\d+)$/,           // attendance_1234567890
        /^settings_(\d+)$/              // settings_1234567890
      ];

      for (const key of allKeys) {
        for (const pattern of eventKeyPatterns) {
          const match = key.match(pattern);
          if (match) {
            const eventId = match[1];
            if (!validEventIds.has(eventId)) {
              orphanedKeys.push(key);
            }
          }
        }
      }

      // 4. Remove orphaned keys
      console.log(`[Storage] Found ${orphanedKeys.length} orphaned keys to remove:`, orphanedKeys);

      for (const key of orphanedKeys) {
        localStorage.removeItem(key);
        console.log(`[Storage] Removed orphaned key: ${key}`);
      }

      // 5. Clean up duplicate storage patterns
      await this.cleanupDuplicateStoragePatterns();

      console.log('[Storage] ✅ Comprehensive storage cleanup complete');

    } catch (error) {
      console.error('[Storage] Error during comprehensive cleanup:', error);
    }
  }

  private async cleanupDuplicateStoragePatterns(): Promise<void> {
    try {
      // Remove duplicate storage patterns that might exist
      const duplicatePatterns = [
        'events_events_current_event',  // Duplicate of events_current_event
        'events_events_events',         // Duplicate of events_events
        'events_created_events'         // Legacy created events tracking
      ];

      for (const key of duplicatePatterns) {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          console.log(`[Storage] Removed duplicate storage pattern: ${key}`);
        }
      }

      // Clean up any remaining legacy connection data
      const connectionKeys = Object.keys(localStorage).filter(key =>
        key.startsWith('last_connection_') ||
        key.startsWith('connection_')
      );

      for (const key of connectionKeys) {
        localStorage.removeItem(key);
        console.log(`[Storage] Removed legacy connection key: ${key}`);
      }

    } catch (error) {
      console.error('[Storage] Error cleaning duplicate patterns:', error);
    }
  }
} 